import React, { useState } from 'react';
import { DateTime } from 'luxon';
import { Form, Formik, useField } from 'formik';
import { object as YupObject, string as YupString } from 'yup';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  Divider,
  TextField,
  FormHelperText,
} from '@material-ui/core';
import { Close, Delete } from '@material-ui/icons';
import { TabletButton } from '@campfire/tablet-button';
import { encodeTime } from '@campfire/hot-date';
import { useCampfireTheme } from '../../../../theme/useCampfireTheme';
import {
  useAddFlexibleSessionInstanceFetch,
  useUpdateFlexibleSessionInstanceFetch,
  useDeleteFlexibleSessionInstanceFetch,
} from '../main-section/roster/flexible-session-functions';

// Native Time Input Component
interface TimeInputProps {
  name: string;
  label: string;
  fullWidth?: boolean;
}

function TimeInput({ name, label, fullWidth = false }: TimeInputProps) {
  const [field, meta] = useField(name);
  const hasError = meta.touched && !!meta.error;

  const timeValue = field.value
    ? typeof field.value === 'string'
      ? field.value
      : typeof field.value === 'object'
      ? DateTime.fromISO(field.value.toString()).toFormat('HH:mm')
      : ''
    : '';

  return (
    <div style={{ width: fullWidth ? '100%' : 'auto' }}>
      <TextField
        fullWidth={fullWidth}
        label={label}
        type='time'
        variant='outlined'
        error={hasError}
        InputLabelProps={{ shrink: true }}
        {...field}
        value={timeValue}
      />
      {hasError && <FormHelperText error>{meta.error}</FormHelperText>}
    </div>
  );
}

interface TextInputProps {
  name: string;
  label: string;
  placeholder?: string;
  fullWidth?: boolean;
}

function TextInput({ name, label, placeholder, fullWidth = false }: TextInputProps) {
  const [field, meta] = useField(name);
  const hasError = meta.touched && !!meta.error;

  return (
    <div style={{ width: fullWidth ? '100%' : 'auto' }}>
      <TextField
        fullWidth={fullWidth}
        label={label}
        placeholder={placeholder}
        variant='outlined'
        error={hasError}
        InputLabelProps={{ shrink: true }}
        {...field}
      />
      {hasError && <FormHelperText error>{meta.error}</FormHelperText>}
    </div>
  );
}

interface FlexibleSessionDialogProps {
  open: boolean;
  onCancel: () => void;
  onSave: () => void;
  sessionId: string;
  sessionName: string;
  sessionStartTime?: string;
  sessionEndTime?: string;
  activityDate: string;
  flexibleSessionInstanceId?: string;
  flexibleSessionName?: string;
}

export function FlexibleSessionDialog({
  open,
  onCancel,
  onSave,
  sessionId,
  sessionName,
  sessionStartTime,
  sessionEndTime,
  activityDate,
  flexibleSessionInstanceId,
  flexibleSessionName,
}: FlexibleSessionDialogProps) {
  const { theme } = useCampfireTheme();
  const addFlexibleSession = useAddFlexibleSessionInstanceFetch();
  const updateFlexibleSession = useUpdateFlexibleSessionInstanceFetch();
  const deleteFlexibleSession = useDeleteFlexibleSessionInstanceFetch();

  const [error, setError] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Format time for the form
  const formatTimeForInput = (time: any): string | null => {
    if (!time) return null;

    if (typeof time === 'string') {
      // Handle ISO string or regular HH:MM string
      return time.includes('T') ? DateTime.fromISO(time).toFormat('HH:mm') : time.substring(0, 5);
    }

    if (typeof time === 'object') {
      // Handle Date or DateTime objects
      try {
        return DateTime.fromISO(time.toString()).toFormat('HH:mm');
      } catch (err) {
        console.error('Error parsing time:', err);
        return null;
      }
    }

    return null;
  };

  // Use custom times and name from flexibleSessionInstance if available, otherwise use session times
  const initialValues = {
    sessionStartTime: formatTimeForInput(sessionStartTime),
    sessionEndTime: formatTimeForInput(sessionEndTime),
    sessionName: flexibleSessionName || '',
  };

  const validationSchema = YupObject().shape({
    sessionStartTime: YupString()
      .nullable()
      .required('Start time is required'),
    sessionEndTime: YupString()
      .nullable()
      .required('End time is required'),
    sessionName: YupString().nullable(),
  });

  const handleDelete = async () => {
    if (!flexibleSessionInstanceId) return;

    setIsDeleting(true);
    setError(null);

    try {
      await deleteFlexibleSession.run({
        id: flexibleSessionInstanceId,
      });

      onSave();
    } catch (err) {
      console.error('Error deleting flexible session:', err);
      setError('Failed to delete customized session. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  const formatDate = (dateString: string) => {
    return DateTime.fromISO(dateString).toFormat('d MMMM yyyy');
  };

  return (
    <Dialog open={open} fullWidth maxWidth='sm' onClose={onCancel} style={{ zIndex: 2000 }}>
      <DialogTitle style={{ padding: '1.6em 2.5em 0.15em 2.5em' }}>
        <Box display='flex' justifyContent='space-between' alignItems='center'>
          <Typography variant='h3' style={{ fontWeight: 500, fontSize: '1.3em' }}>
            Customise Session
          </Typography>
          <IconButton onClick={onCancel}>
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        enableReinitialize
        onSubmit={async (values, { setSubmitting }) => {
          setError(null);

          // Ensure end time is after start time
          if (values.sessionStartTime && values.sessionEndTime) {
            if (values.sessionStartTime >= values.sessionEndTime) {
              setError('End time must be after start time');
              setSubmitting(false);
              return;
            }
          }

          try {
            // Ensure we have valid time strings (should already be validated)
            if (!values.sessionStartTime || !values.sessionEndTime) {
              setError('Both start time and end time are required');
              setSubmitting(false);
              return;
            }

            // Convert time strings to DateTime objects
            const startTime = values.sessionStartTime
              ? DateTime.fromFormat(values.sessionStartTime, 'HH:mm')
              : DateTime.local();

            const endTime = values.sessionEndTime
              ? DateTime.fromFormat(values.sessionEndTime, 'HH:mm')
              : DateTime.local().plus({ hours: 1 });

            // Use encodeTime to get ISO time format
            const startTimeISO = encodeTime(startTime);
            const endTimeISO = encodeTime(endTime);

            if (flexibleSessionInstanceId) {
              // Update existing flexible session instance
              await updateFlexibleSession.run({
                id: flexibleSessionInstanceId,
                startTime: startTimeISO,
                endTime: endTimeISO,
                name: values.sessionName ? values.sessionName : null,
              });
            } else {
              // Add new flexible session instance
              await addFlexibleSession.run({
                sessionId,
                sessionDate: activityDate,
                startTime: startTimeISO,
                endTime: endTimeISO,
                name: values.sessionName ? values.sessionName : null,
              });
            }

            onSave();
          } catch (err) {
            console.error('Error saving flexible session:', err);
            setError('Failed to save changes. Please try again.');
          } finally {
            setSubmitting(false);
          }
        }}
      >
        {({ isSubmitting, values }) => {
          const isEndTimeValid =
            !values.sessionStartTime ||
            !values.sessionEndTime ||
            (values.sessionStartTime && values.sessionEndTime && values.sessionStartTime < values.sessionEndTime);

          return (
            <Form>
              <DialogContent style={{ padding: '0 2.5em 1.5em 2.5em' }}>
                <Typography style={{ color: '#00000099', fontWeight: 400, marginBottom: '1rem' }}>
                  You can customize the time for this specific session instance.
                </Typography>

                <Box marginTop='1rem' marginBottom='1.5rem'>
                  <Typography variant='h5' style={{ fontWeight: 500, fontSize: '1.1em', marginBottom: '0.5rem' }}>
                    {sessionName}
                  </Typography>
                  <Typography style={{ color: theme.color.grey.neutralBrand500 }}>
                    {formatDate(activityDate)}
                  </Typography>
                </Box>

                <Divider style={{ margin: '1rem 0' }} />

                <Box marginTop='1rem' marginBottom='1rem'>
                  <TextInput
                    label='Session Name (Optional)'
                    name='sessionName'
                    fullWidth
                    placeholder='Enter a custom name for this session'
                  />
                </Box>

                <Box display='flex' justifyContent='space-between' marginTop='1.5rem'>
                  <Box width='48%'>
                    <TimeInput name='sessionStartTime' label='Start time' fullWidth />
                  </Box>
                  <Box width='48%'>
                    <TimeInput name='sessionEndTime' label='End time' fullWidth />
                  </Box>
                </Box>

                {error && (
                  <Typography color='error' style={{ marginTop: '1rem', fontSize: '0.875rem' }}>
                    {error}
                  </Typography>
                )}

                {!isEndTimeValid && values.sessionStartTime && values.sessionEndTime && (
                  <Typography color='error' style={{ marginTop: '1rem', fontSize: '0.875rem' }}>
                    Start time must be before end time
                  </Typography>
                )}
              </DialogContent>

              <DialogActions style={{ padding: '0 2.5rem 2.5rem 2.5rem', justifyContent: 'space-between' }}>
                <Box>
                  {flexibleSessionInstanceId && (
                    <TabletButton
                      variant='outlined'
                      color='secondary'
                      startIcon={<Delete />}
                      onClick={handleDelete}
                      disabled={isSubmitting || isDeleting}
                      style={{ textTransform: 'none' }}
                    >
                      Reset Session
                    </TabletButton>
                  )}
                </Box>
                <Box>
                  <Button
                    variant='text'
                    onClick={onCancel}
                    style={{ textTransform: 'none', marginRight: '0.5rem' }}
                    disabled={isSubmitting || isDeleting}
                    type='button'
                  >
                    Cancel
                  </Button>
                  <Button
                    variant='contained'
                    color='primary'
                    disableElevation
                    style={{ textTransform: 'none' }}
                    disabled={isSubmitting || isDeleting || !isEndTimeValid}
                    type='submit'
                  >
                    Save
                  </Button>
                </Box>
              </DialogActions>
            </Form>
          );
        }}
      </Formik>
    </Dialog>
  );
}
